"use client";

import { useEffect } from "react";

import { CopyableContent } from "@/src/components/copyable-content";
import { logger } from "@/src/lib/logging/logger";
import { displayEhrId } from "@/src/lib/resolve-ehr-ids";
import { convertDateStringToUSFormat, formatHumanReadableDate } from "@/src/lib/utils/date-and-phone-utils";
import { Group } from "@/src/models/group";
import { DocumentManagementData } from "@/src/types/document-management-data";

interface DocumentManagementDataDisplayProps {
  documentData: DocumentManagementData | null;
  group: Group | null;
  error?: string;
}

export function DocumentManagementDataDisplay({ documentData, group, error }: DocumentManagementDataDisplayProps) {
  useEffect(() => {
    logger.info("Document Management Data", { documentData });
  }, [documentData]);

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  if (!documentData) {
    return <div className="flex justify-center p-4">No document data available</div>;
  }

  const patientName =
    documentData.patientDetails.givenName && documentData.patientDetails.familyName
      ? `${documentData.patientDetails.givenName} ${documentData.patientDetails.familyName}`
      : "Unknown Patient";

  return (
    <div className="space-y-6 text-sm">
      <div>
        <h3 className="text-md font-semibold mb-3 text-foreground">Patient Information</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Patient Name:</p>
            <CopyableContent text={patientName} />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Patient Account ID:</p>
            <CopyableContent text={displayEhrId(documentData.patientDetails, group) || "N/A"} />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Date of Birth:</p>
            <CopyableContent
              text={
                documentData.patientDetails.dateOfBirth
                  ? convertDateStringToUSFormat(documentData.patientDetails.dateOfBirth)
                  : "N/A"
              }
            />
          </div>
        </div>
      </div>

      {/* Recent Documents */}
      <div>
        <h3 className="text-md font-semibold mb-3 text-foreground">Recently Uploaded Documents</h3>
        {documentData.documentDetails && documentData.documentDetails.length > 0 ? (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 text-sm font-medium">Filename</th>
                  <th className="text-left p-3 text-sm font-medium">Creation Date</th>
                </tr>
              </thead>
              <tbody>
                {documentData.documentDetails.map((document: unknown, index: number) => {
                  const doc = document as { id?: string; filename?: string; createdAt?: string };

                  return (
                    <tr key={doc.id || index} className="border-t hover:bg-muted">
                      <td className="p-3 text-sm font-medium">{doc.filename || "Unknown Document"}</td>
                      <td className="p-3 text-sm">{doc.createdAt ? formatHumanReadableDate(doc.createdAt) : "N/A"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No recent documents available</p>
        )}
      </div>

      {/* Encounters */}
      <div>
        <h3 className="text-md font-semibold mb-3 text-foreground">Previous Encounters</h3>
        {documentData.encountersDetails && documentData.encountersDetails.length > 0 ? (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 text-sm font-medium">Encounter Time</th>
                  <th className="text-left p-3 text-sm font-medium">Provider</th>
                  <th className="text-left p-3 text-sm font-medium">Location</th>
                </tr>
              </thead>
              <tbody>
                {documentData.encountersDetails.map((encounter, index) => (
                  <tr key={encounter.id || index} className="border-t hover:bg-muted">
                    <td className="p-3 text-sm">
                      {encounter.appointmentDatetime
                        ? formatHumanReadableDate(encounter.appointmentDatetime, {
                            includeDate: true,
                            includeTime: true,
                          })
                        : "N/A"}
                    </td>
                    <td className="p-3 text-sm">{encounter.providerName || "Unknown Provider"}</td>
                    <td className="p-3 text-sm">{encounter.locationName || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No encounters available</p>
        )}
      </div>

      {/* Upcoming Sleep Appointments */}
      {documentData.upcomingSleepAppointments && documentData.upcomingSleepAppointments.length > 0 && (
        <div>
          <h3 className="text-md font-semibold mb-3 text-foreground">Upcoming Sleep Appointments</h3>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 text-sm font-medium">Date</th>
                  <th className="text-left p-3 text-sm font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {documentData.upcomingSleepAppointments.map((appt, index) => (
                  <tr key={index} className="border-t hover:bg-muted">
                    <td className="p-3 text-sm">
                      {appt.datetime
                        ? formatHumanReadableDate(appt.datetime, {
                            includeDate: true,
                            includeTime: true,
                            includeTimezone: true,
                          })
                        : "N/A"}
                    </td>
                    <td className="p-3 text-sm">{appt.reason || appt.appointmentType || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
